import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  Popconfirm,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';
import useColumnSearch from 'modules/layoutModule/hooks/useColumnSearch';
import {
  getGtwArticles,
  removeSpecificGtwArticleRequest,
} from '../requests/gtwRequests';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
  isActive: number;
}

const GtwArticles = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState('');

  const confirmDelete = (id: number) => {
    removeSpecificGtwArticleRequest(id).then(() => {
      downloadData();
    });
  };

  const downloadData = () =>
    getGtwArticles().then((response) => {
      if (response) {
        setTableData(response);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (tableData.length === 0) {
      downloadData();
    }
  }, [tableData]);

  const columns = [
    {
      title: 'Data',
      dataIndex: 'dateAdd',
      key: 'dateAdd',
      width: 70,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.DATE_ADD} />
      ),
      sorter: (
        a: { dateAdd: moment.MomentInput },
        b: { dateAdd: moment.MomentInput }
      ) => moment(a.dateAdd).unix() - moment(b.dateAdd).unix(),
    },
    {
      title: 'Tytuł',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      ...useColumnSearch(
        'name',
        'Szukaj po tytule',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.NAME_LINK}
          link={`grunt-to-wiedza/baza-wiedzy/${record.categoryUrl}/${record.url}`}
        />
      ),
    },
    {
      title: 'Okładka',
      dataIndex: 'fileImage1',
      key: 'fileImage1',
      width: 100,
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.IMAGE}
          fieldName="fileImage1"
        />
      ),
    },
    {
      title: 'Kategoria',
      dataIndex: 'categoryName',
      width: 160,
      sorter: (a: { categoryName: string }, b: { categoryName: string }) =>
        a.categoryName.localeCompare(b.categoryName),
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.CATEGORY_TAG}
          fieldName="categoryName"
        />
      ),
    },
    {
      title: 'Licznik',
      dataIndex: 'viewCount',
      width: 100,
      sorter: (a: { viewCount: number }, b: { viewCount: number }) =>
        a.viewCount - b.viewCount,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.VIEWS} />
      ),
    },
    {
      title: 'Aktywna',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '150px',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />
      ),
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (text: string, item: NewsTableTypes) => (
        <Space
          size="middle"
          style={{ display: 'Flex', flexDirection: 'column' }}
        >
          <Tooltip title="Edytuj wpis">
            <Link to={`/gtw-art/${item.id}`}>
              <Button type="primary" shape="circle" icon={<EditOutlined />} />
            </Link>
          </Tooltip>
          <Tooltip title="Usuń wpis">
            <Popconfirm
              title="Czy na pewno chcesz usunąc ten wpis?"
              placement="left"
              onConfirm={() => confirmDelete(item.id)}
              okText="Tak"
              cancelText="Nie"
              disabled={item.isActive === 1}
            >
              <Button type="link" disabled={item.isActive === 1}>
                <DeleteFilled />
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Grunt to Wiedza</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/gtw-art">Baza wiedzy</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default GtwArticles;
